/* sb-dc-tplmerge.js - put split-up text back into one layer.
   2026-09-12 (v3).

   Reported: on a step & repeat template the top line and the bottom line of a
   tile select separately - you click VOTE, then you have to click 2026.

   The design center has no multi-select and no groups: selection is a single
   api.selectedId. So the only way to make a stack of lines behave as one
   object is for it to BE one object, which is what this file does.

   MEASURED (live, tpl 4012d8c2 step-repeat-vote-capitol-8x8sq):
     VOTE  x 24.05 w 13.90  y 8.64  h 6.40  centre 31.00
     2026  x 25.06 w 11.96  y 14.30 h 6.40  centre 31.04
     both Archivo bold, 4.92in, #C32031, align centre, lineHeight 1.15
     y step 14.30 - 8.64 = 5.66 = 4.92 x 1.15 = exactly one line advance
   In other words these were one two-line text block in the artwork and the
   SVG-to-layer converter emitted them as two layers. This puts them back.

   ONLY merges a stack that is unambiguously one block:
     - same font, size, weight, slant, colour, stroke, alignment, lineHeight,
       stretch and decoration;
     - rotation 0 and no curve (rotated and curved text belong to
       sb-dc-tplrot.js / sb-dc-tplcurve.js);
     - horizontal centres within CX_TOL;
     - the next line starts exactly one line advance below, within Y_TOL.
   Anything else - two colours, different sizes, a different gap - is left
   alone. That is deliberate: merging costs the customer the ability to colour
   the lines separately, so it only happens where the lines were never separate
   to begin with.

   ⚠ TIMING, measured twice
   -----------------------
   Removing a layer makes sb-dc-reptile.js re-tile, which rebuilds the
   document, which brings the split layers back, which we merge again. On this
   template:
     v1 (act every tick)     108 removals, 9 rounds - but converged by ~6s
     v2 (wait 0.9s of quiet)  60 removals, 5 rounds - still unconverged at 25s,
                              because the rebuilds never leave three quiet
                              ticks in a row
   v3 acts as soon as the text layers are unchanged for ONE tick: it never
   fires in the middle of a rebuild, but it does not wait for calm that this
   page does not offer. The real cure is fewer rebuilds - that is reptile's
   re-tiling, and a separate job.

   Glyphs do not move: the merged layer keeps the first line's top edge, the
   common centre, and the widest box. Only the selection changes.

   Diagnostics: window.__dcTplMerge() */
(function () {
  'use strict';

  var TICK = 250, WINDOW_MS = 40000, SETTLE = 1, QUIET = 3;
  var CX_TOL = 0.35;        // inches between horizontal centres
  var Y_TOL_FRAC = 0.10;    // of one line advance
  var MAX_LINES = 6;

  var info = { merged: 0, removed: 0, rounds: 0, waits: 0, settled: false, why: '' };
  window.__dcTplMerge = function () { return info; };

  function q(name) {
    try { return new URLSearchParams(location.search).get(name) || ''; }
    catch (e) { return ''; }
  }
  if (!q('tpl')) { info.why = 'no tpl'; info.settled = true; return; }

  function n2(v) { return Math.round((+v || 0) * 100) / 100; }
  function cx(l) { return (+l.xIn || 0) + (+l.widthIn || 0) / 2; }
  function adv(l) { return (+l.fontSizeIn || 0) * (+l.lineHeight || 1); }

  function styleKey(l) {
    return [
      l.fontFamily || '', n2(l.fontSizeIn), l.fill || '', l.stroke || '',
      n2(l.strokeWidthIn), !!l.bold, !!l.italic, l.align || 'center',
      n2(l.lineHeight), n2(l.stretchX), n2(l.stretchY),
      !!l.underline, !!l.strikethrough, l.listStyle || 'none'
    ].join('|');
  }

  function mergeable(l) {
    return l && l.type === 'text' && !l.locked && l.visible !== false
      && !+l.rotation && !+l.curveDeg
      && String(l.text == null ? '' : l.text).length > 0;
  }

  function pass(api, side) {
    if (window.SBDC914?.host.handled(api[side])) return;
    var sd = api[side];
    if (!sd || !Array.isArray(sd.layers)) return 0;

    var pool = sd.layers.filter(mergeable);
    if (pool.length < 2) return 0;

    var buckets = {};
    pool.forEach(function (l) {
      var k = styleKey(l);
      (buckets[k] = buckets[k] || []).push(l);
    });

    var jobs = [];
    Object.keys(buckets).forEach(function (k) {
      var rows = buckets[k].slice().sort(function (a, b) { return (+a.yIn) - (+b.yIn); });
      var used = {};
      rows.forEach(function (head) {
        if (used[head.id]) return;
        var chain = [head], last = head;
        for (;;) {
          var step = adv(last);
          if (!step) break;
          var want = (+last.yIn || 0) + step;
          var tol = step * Y_TOL_FRAC;
          var next = null;
          for (var i = 0; i < rows.length; i++) {
            var c = rows[i];
            if (used[c.id] || c.id === last.id) continue;
            if (chain.indexOf(c) >= 0) continue;
            if (Math.abs((+c.yIn || 0) - want) > tol) continue;
            if (Math.abs(cx(c) - cx(last)) > CX_TOL) continue;
            next = c;
            break;
          }
          if (!next || chain.length >= MAX_LINES) break;
          chain.push(next);
          last = next;
        }
        if (chain.length > 1) {
          chain.forEach(function (c) { used[c.id] = 1; });
          jobs.push(chain);
        }
      });
    });

    if (!jobs.length) return 0;

    jobs.forEach(function (chain) {
      var first = chain[0], last = chain[chain.length - 1];
      var width = 0, sum = 0;
      chain.forEach(function (c) {
        width = Math.max(width, +c.widthIn || 0);
        sum += cx(c);
      });
      var centre = sum / chain.length;
      var text = chain.map(function (c) { return c.text; }).join('\n');
      var height = (+last.yIn || 0) + (+last.heightIn || 0) - (+first.yIn || 0);

      api.updateLayer(first.id, {
        text: text,
        name: text.replace(/\n/g, ' '),
        widthIn: width,
        heightIn: height,
        xIn: centre - width / 2,
        yIn: +first.yIn || 0
      });
      for (var i = 1; i < chain.length; i++) {
        api.removeLayer(chain[i].id);
        info.removed++;
      }
      info.merged++;
    });

    return jobs.length;
  }

  function sigOf(api) {
    var out = '';
    ['front', 'back'].forEach(function (side) {
      var sd = api[side];
      if (!sd || !Array.isArray(sd.layers)) return;
      sd.layers.forEach(function (l) {
        if (!l || l.type !== 'text') return;
        out += '|' + l.id + ',' + (+l.xIn).toFixed(2) + ',' + (+l.yIn).toFixed(2)
             + ',' + String(l.text).length;
      });
    });
    return out;
  }

  var t0 = Date.now(), sig = null, same = 0, quiet = 0;
  var t = setInterval(function () {
    if (Date.now() - t0 > WINDOW_MS) { info.settled = true; clearInterval(t); return; }
    var api = window.__dcApi;
    if (!api || !api.front || typeof api.updateLayer !== 'function'
        || typeof api.removeLayer !== 'function') return;

    var s = sigOf(api);
    if (s !== sig) { sig = s; same = 0; info.waits++; info.settled = false; return; }
    if (++same < SETTLE) return;
    same = 0;

    var n = 0;
    try {
      n += pass(api, 'front');
      if (api.back && api.back.layers && api.back.layers.length) n += pass(api, 'back');
    } catch (e) { info.why = 'error: ' + (e && e.message); }

    if (n) { info.rounds++; quiet = 0; sig = null; info.settled = false; }
    else if (++quiet >= QUIET) info.settled = true;
  }, TICK);
})();
